import { jsx, jsxs } from "react/jsx-runtime";
import React__default, { useMemo, useRef, useEffect } from "react";
import katex from "../node_modules/.pnpm/katex@0.10.2/node_modules/katex/dist/katex.js";
/* empty css                                                                       */
const containsKorean = (text) => {
  return /[\uAC00-\uD7AF\u1100-\u11FF\u3130-\u318F\uA960-\uA97F\uD7B0-\uD7FF]/.test(text);
};
const latexToUnicodeForText = (content) => {
  return content.replace(/\\times\s*/g, "×").replace(/\\div\s*/g, "÷").replace(/\\pm\s*/g, "±").replace(/\\mp\s*/g, "∓").replace(/\\cdot\s*/g, "·").replace(/\\ast\s*/g, "*").replace(/\\;/g, " ").replace(/\\:/g, " ").replace(/\\,/g, " ").replace(/\\!/g, "").replace(/\\quad\s*/g, "  ").replace(/\\qquad\s*/g, "    ").replace(/\\hspace\{[^}]*\}/g, " ").replace(/\\le\s*/g, "≤").replace(/\\leq\s*/g, "≤").replace(/\\ge\s*/g, "≥").replace(/\\geq\s*/g, "≥").replace(/\\ne\s*/g, "≠").replace(/\\neq\s*/g, "≠").replace(/\\alpha\s*/g, "α").replace(/\\beta\s*/g, "β").replace(/\\gamma\s*/g, "γ").replace(/\\pi\s*/g, "π").replace(/\\'/g, "'");
};
const normalizeKatexMath = (math) => {
  let normalized = math;
  normalized = normalized.replace(/\\hspace(\d+\.?\d*)([a-z]+)/g, "\\hspace{$1$2}");
  normalized = normalized.replace(
    /\\underline\\textbf\{([^}]*)\}/g,
    (_, content) => {
      if (containsKorean(content)) {
        const textContent = latexToUnicodeForText(content);
        return `\\underline{\\textbf{\\text{${textContent}}}}`;
      }
      return `\\underline{\\textbf{${content}}}`;
    }
  );
  normalized = normalized.replace(
    /\\underline\{([^}]*)\}/g,
    (match, content) => {
      if (content.startsWith("\\text") || content.startsWith("\\textbf")) {
        return match;
      }
      if (containsKorean(content)) {
        const textContent = latexToUnicodeForText(content);
        return `\\underline{\\text{${textContent}}}`;
      }
      return match;
    }
  );
  return normalized;
};
const renderPlainText = (text, keyPrefix) => {
  if (!text) return null;
  const lines = text.split("\n");
  return lines.map((line, idx) => /* @__PURE__ */ jsxs(React__default.Fragment, { children: [
    line,
    idx < lines.length - 1 && /* @__PURE__ */ jsx("br", {})
  ] }, `${keyPrefix}_line_${idx}`));
};
const decodeBlankMarkers = (text) => {
  return text.replace(/__LBLANK__/g, "{{").replace(/__RBLANK__/g, "}}");
};
const CssFraction = ({
  numerator,
  denominator
}) => {
  const decodedNum = decodeBlankMarkers(numerator);
  const decodedDen = decodeBlankMarkers(denominator);
  return /* @__PURE__ */ jsxs("div", { className: "problem-frac-wrap", children: [
    /* @__PURE__ */ jsx("div", { className: "problem-frac-numerator", children: /* @__PURE__ */ jsx(KatexRenderer, { text: decodedNum }) }),
    /* @__PURE__ */ jsx("div", { className: "problem-frac-denominator", children: /* @__PURE__ */ jsx(KatexRenderer, { text: decodedDen }) })
  ] });
};
const parseTextWithMath = (text) => {
  const combinedReg = /(\$\$[^$]+\$\$|\[FRAC:[^:]*:\/:.*?:FRAC\])/gim;
  const parts = [];
  let lastIndex = 0;
  let match;
  let idx = 0;
  while ((match = combinedReg.exec(text)) !== null) {
    if (match.index > lastIndex) {
      const textContent = text.slice(lastIndex, match.index);
      parts.push({
        type: "text",
        content: textContent,
        key: `text-${idx++}`
      });
    }
    const matched = match[0];
    if (matched.startsWith("[FRAC:")) {
      const fracMatch = matched.match(/\[FRAC:(.*?):\/:(.*?):FRAC\]/);
      if (fracMatch) {
        parts.push({
          type: "frac",
          content: matched,
          key: `frac-${idx++}`,
          numerator: fracMatch[1],
          denominator: fracMatch[2]
        });
      }
    } else if (matched.startsWith("$$")) {
      const mathContent = matched.slice(2, -2);
      parts.push({
        type: "math",
        content: mathContent,
        key: `math-${idx++}`
      });
    }
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    parts.push({
      type: "text",
      content: text.slice(lastIndex),
      key: `text-${idx++}`
    });
  }
  return parts;
};
const KaTeXMath = ({ math, className }) => {
  const containerRef = useRef(null);
  useEffect(() => {
    if (!containerRef.current || !math) return;
    const normalizedMath = normalizeKatexMath(math);
    try {
      katex.render(normalizedMath, containerRef.current, {
        throwOnError: false,
        displayMode: false
      });
    } catch {
      if (containerRef.current) {
        containerRef.current.textContent = `$$${math}$$`;
      }
    }
  }, [math]);
  return /* @__PURE__ */ jsx("span", { ref: containerRef, className });
};
const KatexRenderer = ({ text, className = "" }) => {
  const normalizedText = text.normalize ? text.normalize("NFC") : text;
  const parts = useMemo(() => parseTextWithMath(normalizedText), [normalizedText]);
  if (!text) return null;
  if (parts.length === 0 || parts.length === 1 && parts[0].type === "text") {
    return /* @__PURE__ */ jsx("span", { className, children: renderPlainText(normalizedText, "plain") });
  }
  return /* @__PURE__ */ jsx("span", { className, children: parts.map((part) => {
    if (part.type === "math") {
      return /* @__PURE__ */ jsx(KaTeXMath, { math: part.content }, part.key);
    }
    if (part.type === "frac" && part.numerator !== void 0 && part.denominator !== void 0) {
      return /* @__PURE__ */ jsx(
        CssFraction,
        {
          numerator: part.numerator,
          denominator: part.denominator
        },
        part.key
      );
    }
    return /* @__PURE__ */ jsx("span", { children: renderPlainText(part.content, part.key) }, part.key);
  }) });
};
export {
  KatexRenderer as default,
  normalizeKatexMath
};
//# sourceMappingURL=KatexRenderer.js.map
